import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Link } from 'react-router-dom';
import { Activity, Bot, MessageSquare, Sparkles, ArrowUpRight, Zap, Flame, Check, ShieldCheck } from 'lucide-react';
import { DASHBOARD } from '@/constants/testIds';
import { Sparkline } from '@/components/Sparkline';
import { AgentAvatar } from '@/components/AgentAvatar';

// Deterministic mock usage trend (last 14 days). Replace with real series when usage history backend ships.
function buildSparkSeries(currentTotal = 0) {
  const base = Math.max(2, Math.floor(currentTotal / 6));
  return Array.from({ length: 14 }, (_, i) => {
    const wave = Math.sin(i / 1.7) * 0.4 + 0.6;
    return Math.max(0, Math.round(base * wave + (i * (currentTotal > 0 ? 0.8 : 0.2))));
  });
}

export default function Dashboard() {
  const { user } = useAuth();
  const { data } = useQuery({
    queryKey: ['dashboard-stats'],
    queryFn: async () => (await api.get('/dashboard/stats')).data,
  });

  const { data: adminMe } = useQuery({
    queryKey: ['dashboard-admin-check', user?.user_id],
    queryFn: async () => (await api.get('/admin/me')).data,
    retry: false,
    enabled: !!user,
  });
  const isAdmin = !!adminMe?.user;

  const noConversationsYet = (data?.recent_conversations?.length || 0) === 0;

  const { data: featured } = useQuery({
    queryKey: ['featured-agents-dashboard'],
    queryFn: async () => (await api.get('/agents?featured=true')).data,
    enabled: noConversationsYet,
  });

  const usage = data?.usage;
  const usagePct = usage && !usage.unlimited && usage.limit
    ? Math.min(100, Math.round((usage.used / usage.limit) * 100))
    : 0;

  const stats = [
    { k: 'Conversations', v: data?.conversations ?? 0, icon: MessageSquare, color: '#FF6B1A' },
    { k: 'Messages sent', v: data?.messages ?? 0, icon: Activity, color: '#39FF14' },
    { k: 'Agents built', v: data?.agents_built ?? 0, icon: Bot, color: '#FF6B1A' },
    { k: 'Current plan', v: (data?.plan || user?.plan || 'free').toUpperCase(), icon: Sparkles, color: '#39FF14' },
  ];

  const featuredList = (featured?.items || []).slice(0, 3);

  // 3-step onboarding checklist — only for first-time users
  const isPaid = (data?.plan || user?.plan || 'free') !== 'free';
  const steps = [
    {
      key: 'first-chat',
      title: 'Run your first agent',
      body: 'Pick a signature agent and watch it draft, close, or ship in real time.',
      done: (data?.messages ?? 0) > 0,
      cta: 'Open marketplace',
      href: '/marketplace',
    },
    {
      key: 'first-build',
      title: 'Build your own agent',
      body: 'Name a role, write a prompt, ship in 60 seconds.',
      done: (data?.agents_built ?? 0) > 0,
      cta: 'Open builder',
      href: '/builder',
    },
    {
      key: 'upgrade',
      title: 'Go unlimited',
      body: isPaid ? 'You are on a paid plan — thank you for backing us.' : 'Unlimited messages across every agent for $49/mo.',
      done: isPaid,
      cta: isPaid ? 'Manage plan' : 'View pricing',
      href: '/pricing',
    },
  ];
  const stepsDone = steps.filter((s) => s.done).length;
  const showOnboarding = stepsDone < steps.length;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-20">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-8">
        <div>
          <span className="text-[10px] uppercase tracking-widest text-[#FF6B1A]">// dashboard</span>
          <h1 className="display text-3xl md:text-4xl font-semibold">Welcome back, {user?.name?.split(' ')[0]}.</h1>
          <p className="text-white/60 mt-2 text-sm">Here&apos;s how your agentic stack is performing today.</p>
        </div>
        <div className="flex gap-2">
          <Link to="/builder" className="btn-primary rounded-xl px-4 py-2 text-sm" data-testid="dashboard-new-agent-btn">+ New Agent</Link>
          <Link to="/marketplace" className="btn-secondary rounded-xl px-4 py-2 text-sm">Browse Marketplace</Link>
          {isAdmin && (
            <Link
              to="/admin"
              data-testid="dashboard-admin-cta"
              className="rounded-xl px-4 py-2 text-sm font-medium inline-flex items-center gap-1.5 border border-[#39FF14]/40 text-[#39FF14] bg-[#39FF14]/8 hover:bg-[#39FF14]/15 transition-colors"
              title="Mission Control"
            >
              <ShieldCheck className="h-4 w-4" /> Mission Control
            </Link>
          )}
        </div>
      </div>

      {/* ====================== ONBOARDING CHECKLIST (new users only) ====================== */}
      {showOnboarding && (
        <div className="card-base p-6 mb-6 relative overflow-hidden" data-testid="dashboard-onboarding-checklist">
          <div className="absolute -right-12 -top-16 h-56 w-56 rounded-full bg-[#FF6B1A]/15 blur-3xl pointer-events-none" />
          <div className="absolute -left-12 -bottom-16 h-48 w-48 rounded-full bg-[#39FF14]/10 blur-3xl pointer-events-none" />
          <div className="relative">
            <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
              <div>
                <span className="text-[10px] uppercase tracking-widest text-[#39FF14]">// get to first value</span>
                <h2 className="display text-xl md:text-2xl font-semibold mt-1">3 steps · 60 seconds · first revenue win</h2>
              </div>
              <div className="text-[11px] mono text-white/55 inline-flex items-center gap-2">
                <span className="h-1.5 w-1.5 rounded-full bg-[#39FF14] pulse-green" />
                {stepsDone}/{steps.length} complete
              </div>
            </div>
            <div className="h-1.5 w-full rounded-full bg-white/5 overflow-hidden mb-5">
              <div
                className="h-full rounded-full"
                style={{
                  width: `${(stepsDone / steps.length) * 100}%`,
                  background: 'linear-gradient(90deg, #39FF14, #A8FFCB)',
                  boxShadow: '0 0 14px rgba(57,255,20,0.4)',
                  transition: 'width 600ms cubic-bezier(.2,.9,.2,1)',
                }}
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {steps.map((s, i) => (
                <div
                  key={s.key}
                  data-testid={`dashboard-onboarding-step-${s.key}`}
                  className={`hairline rounded-xl p-4 relative transition-colors ${s.done ? 'bg-[#39FF14]/5 border-[#39FF14]/35' : 'hover:border-[#FF6B1A]/35'}`}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <div className={`h-6 w-6 rounded-full grid place-items-center text-[10px] font-semibold ${s.done ? 'bg-[#39FF14] text-black' : 'bg-white/5 text-white/70 border border-white/10'}`}>
                      {s.done ? <Check className="h-3 w-3" /> : i + 1}
                    </div>
                    <div className={`text-xs uppercase tracking-widest mono ${s.done ? 'text-[#39FF14]' : 'text-white/55'}`}>
                      {s.done ? 'complete' : `step 0${i + 1}`}
                    </div>
                  </div>
                  <div className="display text-base font-semibold">{s.title}</div>
                  <p className="text-xs text-white/60 mt-1 min-h-[32px]">{s.body}</p>
                  <Link
                    to={s.href}
                    data-testid={`dashboard-onboarding-cta-${s.key}`}
                    className={`mt-3 inline-flex items-center gap-1 text-xs ${s.done ? 'text-white/50 hover:text-white/80' : 'text-[#FF6B1A] hover:text-[#FFB17A]'}`}
                  >
                    {s.cta} <ArrowUpRight className="h-3 w-3" />
                  </Link>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {stats.map(({ k, v, icon: Icon, color }) => (
          <div key={k} data-testid={DASHBOARD.kpiCard} className="card-base card-hover p-5">
            <div className="h-9 w-9 rounded-lg grid place-items-center hairline mb-3" style={{ boxShadow: `0 0 16px ${color}33` }}>
              <Icon className="h-4 w-4" style={{ color }} />
            </div>
            <div className="display text-2xl font-semibold">{v}</div>
            <div className="text-xs uppercase tracking-widest text-white/40 mt-1">{k}</div>
          </div>
        ))}
      </div>

      {/* Usage card */}
      <div className="card-base p-6 mb-6" data-testid="dashboard-usage-card">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[#FF6B1A]">// usage this month</div>
            <h2 className="display text-lg font-semibold mt-1">{usage?.period || ''}</h2>
          </div>
          {usage?.unlimited ? (
            <span className="text-[11px] uppercase tracking-widest px-2 py-1 rounded-full border border-[#39FF14]/35 text-[#39FF14] bg-[#39FF14]/8 inline-flex items-center gap-1">
              <Zap className="h-3 w-3" /> Unlimited
            </span>
          ) : (
            <Link to="/pricing" className="btn-primary rounded-lg px-3 py-1.5 text-xs inline-flex items-center gap-1">
              Upgrade <ArrowUpRight className="h-3 w-3" />
            </Link>
          )}
        </div>
        <div className="mt-4 grid lg:grid-cols-[1fr_auto] gap-6 items-end">
          <div>
            <div className="flex items-baseline justify-between mb-2">
              <div className="display text-3xl font-semibold">
                {usage?.used ?? 0}
                {!usage?.unlimited && <span className="text-base text-white/40"> / {usage?.limit ?? 0}</span>}
              </div>
              <div className="text-xs text-white/45">messages</div>
            </div>
            <div className="h-2 w-full rounded-full bg-white/5 overflow-hidden">
              <div
                className="h-full rounded-full transition-all"
                style={{
                  width: usage?.unlimited ? '100%' : `${usagePct}%`,
                  background: usage?.unlimited
                    ? 'linear-gradient(90deg, #39FF14, #A8FFCB)'
                    : 'linear-gradient(90deg, #FF6B1A, #FFB17A)',
                  boxShadow: '0 0 18px rgba(255,107,26,0.45)',
                }}
              />
            </div>
            <p className="text-xs text-white/55 mt-3">
              {usage?.unlimited
                ? 'You\u2019re on a paid plan — message all you want.'
                : `${(usage?.limit ?? 50) - (usage?.used ?? 0)} messages remaining this period. Upgrade for unlimited.`}
            </p>
          </div>
          <div className="flex flex-col items-end shrink-0">
            <div className="text-[10px] uppercase tracking-widest text-white/45 mb-1">14-day trend</div>
            <Sparkline data={buildSparkSeries(data?.messages ?? 0)} width={220} height={56} />
          </div>
        </div>
      </div>

      {/* Recent conversations OR featured agents */}
      {noConversationsYet ? (
        <div className="card-base p-6" data-testid="dashboard-featured-strip">
          <div className="flex items-end justify-between mb-4 flex-wrap gap-2">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-[#39FF14]">// fast wins</div>
              <h2 className="display text-lg font-semibold">Signature agents to try first</h2>
            </div>
            <Link to="/marketplace" className="btn-ghost rounded-xl px-3 py-2 text-xs inline-flex items-center gap-1">
              Browse all 18 <ArrowUpRight className="h-3 w-3" />
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {featuredList.length === 0
              ? Array.from({ length: 3 }).map((_, i) => (
                  <div key={`skel-onb-${i}`} className="hairline rounded-xl p-4 h-28 animate-pulse" />
                ))
              : featuredList.map((a) => (
                  <Link
                    key={a.agent_id}
                    to={`/agents/${a.agent_id}`}
                    data-testid="dashboard-featured-agent-card"
                    className="hairline hover:border-[#FF6B1A]/45 hover:bg-white/5 rounded-xl p-4 transition-colors group"
                  >
                    <div className="flex items-center gap-3">
                      <AgentAvatar name={a.name} size={40} />
                      <div className="min-w-0">
                        <div className="display text-sm font-semibold truncate">{a.name}</div>
                        <div className="text-[11px] text-white/55 truncate">{a.role}</div>
                      </div>
                    </div>
                    <p className="text-xs text-white/65 mt-3 line-clamp-2">{a.description}</p>
                    <div className="mt-3 inline-flex items-center gap-1 text-[11px] text-[#FF6B1A] group-hover:text-[#FFB17A]">
                      Try now <ArrowUpRight className="h-3 w-3" />
                    </div>
                  </Link>
                ))}
          </div>
        </div>
      ) : (
        <div className="card-base p-6" data-testid={DASHBOARD.usageTable}>
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-[#39FF14]">// recent</div>
              <h2 className="display text-lg font-semibold">Recent conversations</h2>
            </div>
            <Link to="/marketplace" className="btn-ghost text-xs rounded px-2 py-1 inline-flex items-center gap-1">
              Start new <ArrowUpRight className="h-3 w-3"/>
            </Link>
          </div>
          <ul className="divide-y divide-border/60">
            {data.recent_conversations.map((c) => (
              <li key={c.conversation_id} className="py-3 flex items-center justify-between gap-3">
                <div className="min-w-0 flex items-center gap-3">
                  <Flame className="h-4 w-4 text-[#39FF14] shrink-0" />
                  <div className="min-w-0">
                    <div className="text-sm truncate">{c.title || 'Untitled'}</div>
                    <div className="text-[11px] text-white/45 truncate">
                      {c.agent_name} · {new Date(c.updated_at).toLocaleString()}
                    </div>
                  </div>
                </div>
                <Link to={`/chat?agent=${c.agent_id}&conv=${c.conversation_id}`} className="btn-secondary text-xs rounded-lg px-3 py-1.5">
                  Open
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
